import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { changeLanguage, getCurrentLanguage, SUPPORTED_LANGUAGES } from '../../../i18n';

import styles from './LanguageSwitcher.module.scss';

const LanguageSwitcher = ({ variant = 'dropdown', showFlag = false, showName = true }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  const currentLang = getCurrentLanguage();

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLanguageChange = (langCode) => {
    changeLanguage(langCode);
    setIsOpen(false);
  };

  const currentLanguage = SUPPORTED_LANGUAGES[currentLang] || SUPPORTED_LANGUAGES.en;

  // Format language display as "NativeName (EnglishName)"
  const formatLanguageDisplay = (lang) => `${lang.nativeName} (${lang.name})`;

  if (variant === 'buttons') {
    return (
      <div className={styles.buttonGroup}>
        {Object.entries(SUPPORTED_LANGUAGES).map(([code, lang]) => (
          <button
            key={code}
            type="button"
            className={`${styles.langButton} ${currentLang === code ? styles.active : ''}`}
            onClick={() => handleLanguageChange(code)}
            aria-label={`Switch to ${lang.name}`}
          >
            {showFlag && lang.flag && <span className={styles.flag}>{lang.flag}</span>}
            {showName && <span className={styles.langName}>{formatLanguageDisplay(lang)}</span>}
          </button>
        ))}
      </div>
    );
  }

  return (
    <div className={styles.languageSwitcher} ref={dropdownRef}>
      <button
        type="button"
        className={styles.trigger}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-label={t('common.language.select')}
      >
        {showFlag && currentLanguage.flag && <span className={styles.flag}>{currentLanguage.flag}</span>}
        {showName && <span className={styles.langName}>{formatLanguageDisplay(currentLanguage)}</span>}
        <span className={`${styles.chevron} ${isOpen ? styles.open : ''}`}>▼</span>
      </button>

      {isOpen && (
        <ul className={styles.dropdown} role="listbox" aria-label={t('common.language.select')}>
          {Object.entries(SUPPORTED_LANGUAGES).map(([code, lang]) => (
            <li key={code} role="option" aria-selected={currentLang === code}>
              <button
                type="button"
                className={`${styles.option} ${currentLang === code ? styles.selected : ''}`}
                onClick={() => handleLanguageChange(code)}
              >
                {showFlag && lang.flag && <span className={styles.flag}>{lang.flag}</span>}
                <span className={styles.langName}>{formatLanguageDisplay(lang)}</span>
                {currentLang === code && <span className={styles.checkmark}>✓</span>}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export { LanguageSwitcher };
